import guitarDb from "@tombatossals/chords-db/lib/guitar.json";

export type Instrument = "guitar" | "bass";

export interface ChordPosition {
  frets: number[];
  fingers: number[];
  baseFret: number;
  barres: number[];
  midi?: number[];
  capo?: boolean;
}

interface ChordboxData {
  chord: [number, number | "x"][];
  barres: { fromString: number; toString: number; fret: number }[];
  position: number;
}

// Maps chord-name key to chords-db object key
// Note: chords-db uses "Csharp"/"Fsharp" not "C#"/"F#"
const KEY_MAP: Record<string, string> = {
  "C#": "Csharp",
  "Db": "Csharp",
  "D#": "Eb",
  "E#": "F",
  "F#": "Fsharp",
  "Gb": "Fsharp",
  "G#": "Ab",
  "A#": "Bb",
  "B#": "C",
};

// Maps chord suffix from chord name to chords-db suffix
const SUFFIX_MAP: Record<string, string> = {
  "": "major",
  "m": "minor",
  "min": "minor",
  "maj": "major",
  "7": "7",
  "maj7": "maj7",
  "M7": "maj7",
  "m7": "m7",
  "min7": "m7",
  "dim": "dim",
  "dim7": "dim7",
  "aug": "aug",
  "aug7": "aug7",
  "sus2": "sus2",
  "sus4": "sus4",
  "6": "6",
  "m6": "m6",
  "9": "9",
  "m9": "m9",
  "maj9": "maj9",
  "11": "11",
  "m11": "m11",
  "maj11": "maj11",
  "13": "13",
  "maj13": "maj13",
  "add9": "add9",
  "madd9": "madd9",
  "m7b5": "m7b5",
  "7b5": "7b5",
  "7sus4": "7sus4",
  "mmaj7": "mmaj7",
};

// Slash chord suffixes that chords-db handles
const SLASH_SUFFIXES = new Set([
  "/E", "/F", "/F#", "/G", "/G#", "/A", "/Bb", "/B", "/C", "/C#",
  "m/B", "m/C", "m/C#", "/D", "m/D", "/D#", "m/D#", "m/E", "m/F",
  "m/F#", "m/G", "m/G#",
]);

/**
 * Parse a chord name like "C#m7", "D/F#", "Am/G" into {key, suffix}
 * for chords-db lookup.
 */
export function parseChordName(chordName: string): { key: string; suffix: string } | null {
  // Strip slash chord bass note to check slash suffix
  // e.g. "C/E" -> key="C", suffix="/E"
  // e.g. "Am/G" -> key="A", suffix="m/G"
  const slashMatch = chordName.match(/^([A-G][#b]?)(.*\/[A-G][#b]?)$/);
  if (slashMatch) {
    const rawKey = slashMatch[1];
    const suffix = slashMatch[2]; // e.g. "/E" or "m/G"
    const key = KEY_MAP[rawKey] ?? rawKey;
    if (SLASH_SUFFIXES.has(suffix)) {
      return { key, suffix };
    }
    // If slash suffix not in db, fall back to base chord (strip slash part)
    const baseSuffix = suffix.replace(/\/[A-G][#b]?$/, "");
    const mappedSuffix = SUFFIX_MAP[baseSuffix];
    if (mappedSuffix) return { key, suffix: mappedSuffix };
    return { key, suffix: "major" };
  }

  // Non-slash chord: parse key + suffix
  const match = chordName.match(/^([A-G][#b]?)(.*)$/);
  if (!match) return null;

  const rawKey = match[1];
  const suffixStr = match[2];
  const key = KEY_MAP[rawKey] ?? rawKey;
  const suffix = SUFFIX_MAP[suffixStr] ?? null;

  if (!suffix) return null;
  return { key, suffix };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GUITAR_DB: any = guitarDb;

/** Look up the raw guitar chord position. */
function getRawPosition(chordName: string): ChordPosition | null {
  const parsed = parseChordName(chordName);
  if (!parsed) return null;

  const chords = (GUITAR_DB.chords as Record<string, { key: string; suffix: string; positions: ChordPosition[] }[]>)[parsed.key];
  if (!chords) return null;

  const chord = chords.find((c) => c.suffix === parsed.suffix);
  if (!chord || chord.positions.length === 0) return null;

  return chord.positions[0];
}

/**
 * Get the chord position for guitar or bass.
 */
export function getChordsDbPosition(
  chordName: string,
  instrument: Instrument
): ChordPosition | null {
  if (instrument === "guitar") {
    return getRawPosition(chordName);
  }
  if (instrument === "bass") {
    return getBassPosition(chordName);
  }
  return null;
}

/**
 * Bass: use the top 4 strings of guitar (strings 6,5,4,3 = E,A,D,G),
 * matching standard bass tuning. These are frets indices 0–3.
 */
function getBassPosition(chordName: string): ChordPosition | null {
  const guitarPos = getRawPosition(chordName);
  if (!guitarPos) return null;

  // Top 4 guitar strings: indices 0–3 (string 6=E, 5=A, 4=D, 3=G)
  const bassFrets = guitarPos.frets.slice(0, 4);
  const bassFingers = guitarPos.fingers.slice(0, 4);

  // Keep only barres that land within the top 4 string indices (0–3)
  const bassBarres = guitarPos.barres.filter((barFret) =>
    bassFrets.some((f) => f === barFret)
  );

  // If all 4 strings are muted/open with no useful notes, still show it
  return {
    frets: bassFrets,
    fingers: bassFingers,
    baseFret: guitarPos.baseFret,
    barres: bassBarres,
    midi: guitarPos.midi,
  };
}

/**
 * Convert a chords-db position to @chordbook/charts format.
 */
export function convertToChordboxFormat(
  position: ChordPosition,
  numStrings: number
): ChordboxData {
  const chord: [number, number | "x"][] = position.frets.map((fret, i) => {
    const stringNum = numStrings - i;
    return [stringNum, fret === -1 ? "x" : fret] as [number, number | "x"];
  });

  const barres = position.barres.map((barFret) => {
    const stringIndices = position.frets
      .map((f, i) => (f === barFret ? i : -1))
      .filter((i) => i !== -1);

    if (stringIndices.length === 0) {
      return { fromString: numStrings, toString: 1, fret: barFret };
    }

    const fromString = numStrings - Math.min(...stringIndices);
    const toString = numStrings - Math.max(...stringIndices);
    return { fromString, toString, fret: barFret };
  });

  return {
    chord,
    barres,
    position: position.baseFret,
  };
}

/**
 * Check if a chord has data for the given instrument.
 */
export function hasChordData(chordName: string, instrument: Instrument): boolean {
  return getChordsDbPosition(chordName, instrument) !== null;
}

export const INSTRUMENT_STRINGS: Record<"guitar" | "bass", number> = {
  guitar: 6,
  bass: 4,
};
